import React from 'react';
import { Box, Typography } from '@mui/material';
import Barcode from 'react-barcode';
import type { ProcessedTicketData } from '../../../types/processedTicket';
import type { ScannerConfig } from '../../../types/features/games';
import { useTicketCardLogic } from '../../../hooks/logic/useTicketCardLogic';
import { translateBarcodeFormat } from '../../../utils/barcodeFormatTranslator';
import { normalizeGameImageUrl } from '../../../utils/normalizers/gameImageUrl';

type CaseSimulatorDisplayCaseGridProps = {
  exportLayout?: 'current' | 'tabloid';
  rootRef?: React.Ref<HTMLDivElement>;
  processedPacks: ProcessedTicketData[];
  scannerConfig?: ScannerConfig | null;
  storeId: string;
  imageUrlOverrides?: Map<string, string>;
};

type DisplayCaseTileProps = {
  exportLayout?: 'current' | 'tabloid';
  pack: ProcessedTicketData;
  scannerConfig?: ScannerConfig | null;
  storeId: string;
  imageFrameHeight?: number;
  onMeasureImageHeight?: (height: number) => void;
  imageUrl?: string;
};

const getBarcodeSymbology = (scannerFormats?: string[]) => {
  const firstFormat = scannerFormats?.find((format) => format?.trim().length > 0);
  return translateBarcodeFormat(firstFormat);
};

const TABLOID_CARDS_PER_PAGE = 15;
const TABLOID_CARD_HEIGHT_IN = 2.814;
const TABLOID_HEADER_HEIGHT_IN = 1.35;

const TabloidTestSheetHeader = () => (
  <Box
    sx={{
      alignItems: 'flex-start',
      boxSizing: 'border-box',
      display: 'flex',
      height: `${TABLOID_HEADER_HEIGHT_IN}in`,
      justifyContent: 'space-between',
      px: '0.18in',
      pt: '0.02in',
    }}
  >
    <Box sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="img"
        src="/lottoreco-wordmark.svg"
        alt="LottoReco"
        sx={{ height: '0.896in', width: '4.69in' }}
      />
      <Typography sx={{ color: '#223b75', fontSize: '15.6pt', fontWeight: 700, lineHeight: 1, mt: '0.1in' }}>
        Lottery Management Software for Retailers
      </Typography>
    </Box>

    <Box
      sx={{
        alignItems: 'center',
        display: 'grid',
        gap: '0.18in',
        gridTemplateColumns: '1.75in 1.08in',
      }}
    >
      <Typography
        sx={{ color: '#222b45', fontSize: '12pt', fontWeight: 600, lineHeight: 1.25, textAlign: 'right' }}
      >
        Track lottery sales,<br />
        payouts, inventory,<br />
        and missing packs<br />
        in one place.
      </Typography>
      <Box
        className="case-simulator-test-sheet-qr"
        component="img"
        src="/lottery-test-sheet-vcard-qr.svg"
        alt="Scan to save Anuj Khandelwal's LottoReco contact"
        sx={{ height: '1.08in', width: '1.08in' }}
      />
    </Box>
  </Box>
);

const DisplayCaseTile: React.FC<DisplayCaseTileProps> = React.memo(({
  exportLayout,
  pack,
  scannerConfig,
  storeId,
  imageFrameHeight,
  onMeasureImageHeight,
  imageUrl,
}) => {
  const isExportLayout = Boolean(exportLayout);
  const isTabloidLayout = exportLayout === 'tabloid';
  const {
    barcodeValue,
    currentTicketNumber,
    ticketInfo,
  } = useTicketCardLogic({ pack, storeId });
  const barcodeFormat = getBarcodeSymbology(scannerConfig?.barcode_scanner_formats || pack.barcode_scanner_formats);

  return (
    <Box
      className="case-simulator-display-case-tile"
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #d9e0ea',
        borderRadius: '8px',
        overflow: 'hidden',
        breakInside: 'avoid',
        display: 'flex',
        flexDirection: 'column',
        ...(isExportLayout
          ? {
              borderRadius: '0.04in',
              boxSizing: 'border-box',
              height: isTabloidLayout ? `${TABLOID_CARD_HEIGHT_IN}in` : undefined,
              width: isTabloidLayout ? '3.2in' : '4in',
            }
          : {}),
      }}
    >
      <Box
        className="case-simulator-display-case-image-frame"
        sx={{
          backgroundColor: '#f7f9fc',
          borderBottom: '1px solid #e5ebf2',
          height: imageFrameHeight ? `${imageFrameHeight}px` : 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'hidden',
          ...(isExportLayout
            ? {
                aspectRatio: isTabloidLayout ? undefined : '1 / 0.86',
                height: isTabloidLayout ? '2.05in' : 'auto',
                maxHeight: 'none',
                position: 'relative',
                flexShrink: 0,
              }
            : {}),
        }}
      >
        {imageUrl ? (
          <Box
            className="case-simulator-display-case-image"
            component="img"
            src={imageUrl}
            alt={pack.game_name}
            onLoad={(event) => {
              if (!onMeasureImageHeight) {
                return;
              }

              onMeasureImageHeight(event.currentTarget.getBoundingClientRect().height);
            }}
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              ...(isExportLayout
                ? {
                    left: 0,
                    position: 'absolute',
                    top: 0,
                  }
                : {}),
            }}
          />
        ) : (
          <Typography sx={{ color: '#8a94a6', fontSize: 14 }}>No image</Typography>
        )}
      </Box>

      <Box
        className="case-simulator-display-case-barcode-area"
        sx={{
          px: 1,
          pt: 0.35,
          pb: 0.5,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          minHeight: 84,
          ...(isExportLayout
            ? {
                flex: 1,
                minHeight: 0,
                px: 0,
                pt: isTabloidLayout ? '0.05in' : 'calc(0.04in + 10px)',
                pb: isTabloidLayout ? '0.03in' : '0.06in',
              }
            : {}),
        }}
      >
        {ticketInfo.isSoldOut ? (
          <Typography sx={{ color: '#b42318', fontWeight: 800, border: '2px solid #f04438', px: 1.5, py: 0.75 }}>
            SOLD OUT
          </Typography>
        ) : barcodeValue && barcodeFormat ? (
          <>
            <Typography
              className="case-simulator-display-case-barcode-number"
              sx={{
                alignSelf: 'center',
                color: '#1f2a44',
                fontFamily: 'monospace',
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1.1,
                textAlign: 'center',
                ...(isExportLayout
                  ? {
                      fontSize: isTabloidLayout ? '10pt' : '12pt',
                      lineHeight: 1,
                      mb: isTabloidLayout ? '5px' : 0,
                      width: '2.5in',
                    }
                  : {}),
              }}
            >
              {currentTicketNumber}
            </Typography>
            <Box
              className="case-simulator-display-case-barcode"
              sx={{
                alignSelf: 'center',
                display: 'flex',
                height: 58,
                justifyContent: 'center',
                mt: '10px',
                overflow: 'hidden',
                width: 240,
                ...(isExportLayout
                  ? {
                      height: isTabloidLayout ? '0.42in' : '0.6in',
                      flexShrink: 0,
                      mb: isTabloidLayout ? '5px' : 0,
                      mt: 0,
                      width: isTabloidLayout ? '2.25in' : '2.5in',
                    }
                  : {}),
              }}
            >
              <Barcode
                value={barcodeValue}
                format={barcodeFormat}
                width={isTabloidLayout ? 1 : 2}
                height={isTabloidLayout ? 38 : 53}
                displayValue={false}
                margin={0}
                lineColor="#000000"
                background="#ffffff"
              />
            </Box>
          </>
        ) : (
          <Typography sx={{ color: '#b42318', fontSize: 12 }}>Barcode not available</Typography>
        )}
      </Box>
    </Box>
  );
});

DisplayCaseTile.displayName = 'DisplayCaseTile';

const CaseSimulatorDisplayCaseGrid: React.FC<CaseSimulatorDisplayCaseGridProps> = ({
  exportLayout,
  processedPacks,
  rootRef,
  scannerConfig,
  storeId,
  imageUrlOverrides,
}) => {
  const isExportLayout = Boolean(exportLayout);
  const isTabloidLayout = exportLayout === 'tabloid';
  const [imageFrameHeight, setImageFrameHeight] = React.useState<number>();
  const handleMeasureImageHeight = React.useCallback((height: number) => {
    if (height > 0) {
      setImageFrameHeight(Math.round(height));
    }
  }, []);

  if (processedPacks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Activated Packs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Activate packs to build a display case sheet
        </Typography>
      </Box>
    );
  }

  const renderTiles = (packs: ProcessedTicketData[], pageStartIndex = 0) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 1.5,
        alignItems: 'start',
        ...(isExportLayout
          ? {
              gridTemplateColumns: isTabloidLayout ? 'repeat(3, 3.2in)' : 'repeat(4, 4in)',
              gridAutoRows: isTabloidLayout ? `${TABLOID_CARD_HEIGHT_IN}in` : undefined,
              gap: isTabloidLayout ? '0.12in 0.2in' : '0.18in 0.6667in',
            }
          : {}),
      }}
    >
      {packs.map((pack, index) => (
        <DisplayCaseTile
          key={pack.pack_id}
          exportLayout={exportLayout}
          pack={pack}
          scannerConfig={scannerConfig}
          storeId={storeId}
          imageFrameHeight={imageFrameHeight}
          onMeasureImageHeight={pageStartIndex + index === 0 ? handleMeasureImageHeight : undefined}
          imageUrl={imageUrlOverrides?.get(pack.pack_id) || normalizeGameImageUrl(pack.image_url)}
        />
      ))}
    </Box>
  );

  const tabloidPages = isTabloidLayout
    ? Array.from(
        { length: Math.ceil(processedPacks.length / TABLOID_CARDS_PER_PAGE) },
        (_, pageIndex) => processedPacks.slice(
          pageIndex * TABLOID_CARDS_PER_PAGE,
          (pageIndex + 1) * TABLOID_CARDS_PER_PAGE,
        ),
      )
    : [];

  return (
    <Box
      ref={rootRef}
      className={`case-simulator-display-case-print${isExportLayout ? ' case-simulator-display-case-export' : ''}`}
      style={{
        '--display-case-image-height': imageFrameHeight ? `${imageFrameHeight}px` : undefined,
      } as React.CSSProperties}
      sx={{
        backgroundColor: '#ffffff',
        width: isTabloidLayout ? '10in' : isExportLayout ? '18in' : undefined,
      }}
    >
      {isTabloidLayout
        ? tabloidPages.map((pagePacks, pageIndex) => (
            <Box
              key={pagePacks[0].pack_id}
              data-test-sheet-page="true"
              sx={{
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                height: '16in',
                overflow: 'hidden',
                width: '10in',
              }}
            >
              <TabloidTestSheetHeader />
              <Box sx={{ height: '0.1in' }} />
              {renderTiles(pagePacks, pageIndex * TABLOID_CARDS_PER_PAGE)}
            </Box>
          ))
        : renderTiles(processedPacks)}
    </Box>
  );
};

export default React.memo(CaseSimulatorDisplayCaseGrid);
